import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryForm from "@/components/dictionaries/DictionaryForm";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { isRequired } from "@/lib/form";
import { isNotId } from "@/lib/utils/isNotId";
import { useStoreGroupDictionary } from "@/modules/storeGroups/hooks/useStoreGroupDictionary";
import { displayStoreGroup } from "@/schemas/storeGroup";
import { ReadStoreGroup, WriteStoreGroup } from "@/types/storeGroup";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info } from "lucide-react";
import { PropsWithChildren, useCallback, useState } from "react";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SaveButton from "@/components/SaveButton";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useCreateStoreGroup } from "@/modules/storeGroups/hooks/useCreateStoreGroup";

export default function StoreGroupCreator({ children }: PropsWithChildren) {
  const id = "new";
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const config = useStoreGroupDictionary();
  const labels = config.getLabels(t);
  const { Icon, ns } = config;
  const { can_create } = useModulePermissions();

  const handleCreateSuccess = useCallback(
    (response: ReadStoreGroup) => {
      navigate(`${config.path}/${response.id}`);
      toast({
        title: labels.success.create,
      });
      setOpen(false);
    },
    [config.path, labels.success.create, navigate, toast],
  );

  // Use mutation hook for create operation
  const mutation = useCreateStoreGroup({
    onSuccess: handleCreateSuccess,
    errorTitle: labels.errors.create,
  });

  const form = useForm<WriteStoreGroup>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues ||
      {}) as DefaultValues<WriteStoreGroup>,
    disabled: mutation.isPending || !can_create,
  });

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        form.reset();
      }
      setOpen(open);
    },
    [form],
  );

  return (
    <DictionaryRecordContextProvider
      value={{
        id,
        form,
        mutation,
        config,
      }}
    >
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTrigger asChild>{children}</DialogTrigger>
        {open && (
          <DialogContent className="w-4/5 max-w-xl">
            <DictionaryForm>
              <DialogHeader>
                <DialogTitle className="flex flex-row gap-2 items-center">
                  <Icon />
                  {t("storeGroups.dialog.title")}
                </DialogTitle>
              </DialogHeader>
              <Fieldset className="gap-4">
                {Object.entries(displayStoreGroup.unwrap().shape)
                  .filter(([key]) => isNotId(key))
                  .map(([key, field], index) => (
                    <Field
                      key={index}
                      name={key}
                      label={t(`${ns}.fields.${key}`)}
                      required={isRequired(field)}
                      render={({ field }) => (
                        <Input
                          disabled={mutation.isPending}
                          className="w-full"
                          {...field}
                        />
                      )}
                    />
                  ))}
              </Fieldset>

              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Info className="w-4 h-4" />
                <span>{t("storeGroups.dialog.info")}</span>
              </div>
              <DialogFooter className="gap-3 mt-3">
                <DialogClose asChild>
                  <Button variant="link" disabled={mutation.isPending}>
                    {t("storeGroups.dialog.cancel")}
                  </Button>
                </DialogClose>
                <SaveButton />
              </DialogFooter>
            </DictionaryForm>
          </DialogContent>
        )}
      </Dialog>
    </DictionaryRecordContextProvider>
  );
}
